import React, { useMemo, useState } from "react";
import { formatUSD, formatPercent } from "@/utils/formatters";
import { formatDateKeyDisplay } from "../../lib/timezone";
import type { LeaderboardEntry } from "../../types";
import CohortRecoveryCard from "./CohortRecoveryCard";
import HoverTooltip from "./HoverTooltip";
import { SECTOR_WEIGHTS, POINTS_PER_RANK, type SectorFilter } from "./constants";
import { computeRepJourney, computeFederer } from "./scoreBreakdownUtils";

// =============================================================================
// CALCULATIONS DRAWER — "Show Your Work" Panel
// =============================================================================
// PURPOSE: Exec-only panel that explains exactly how every score, rank, and
// prestige point is calculated. Designed for amnesia: if you forget everything
// about the prestige system, open this panel and you'll know within 5 minutes.
//
// RETARDPROOF: Every number has a label, period, and baseline. Every concept
// has a plain-English explanation. No jargon without inline definition.
// =============================================================================

interface CalculationsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  activeSector: SectorFilter;
  sectorEntries: Record<SectorFilter, LeaderboardEntry[]>;
  activeLeaderboard: LeaderboardEntry[];
  dateLabel: string;
  timezone?: string;
}

// --- Collapsible Section ---
const Section: React.FC<{
  title: string;
  subtitle: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}> = ({ title, subtitle, defaultOpen = false, children }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-black/[0.06] last:border-b-0">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-black/[0.02] transition-colors text-left"
      >
        <div>
          <h3 className="token-18 text-glass">{title}</h3>
          <p className="token-13 text-glass-muted mt-0.5">{subtitle}</p>
        </div>
        <svg
          className={`w-4 h-4 text-glass-muted transition-transform ${isOpen ? "rotate-180" : ""}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {isOpen && <div className="px-4 pb-4">{children}</div>}
    </div>
  );
};

// --- Note Box (retardproof: explains what a number means) ---
const Note: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="mt-3 p-2.5 rounded-lg bg-ds-amber-bg border border-ds-amber/20">
    <p className="token-13 text-glass-secondary leading-relaxed">{children}</p>
  </div>
);

// HoverTooltip — imported from ./HoverTooltip (extracted for LeaderboardPage reuse)

// --- Weight blocks visual ---
const WeightBlocks: React.FC<{
  weights: { label: string; pct: number; desc: string }[];
}> = ({ weights }) => (
  <div className="flex gap-2 mt-3">
    {weights.map((w) => (
      <div
        key={w.label}
        className="flex-1 rounded-md p-2 bg-black/[0.03] border border-black/[0.06] text-center"
      >
        <p className="token-15 text-glass">{w.pct}%</p>
        <p className="token-13 text-glass-secondary mt-0.5">{w.label}</p>
        <p className="token-13 text-glass-muted">{w.desc}</p>
      </div>
    ))}
  </div>
);

// SECTOR_WEIGHTS + POINTS_PER_RANK imported from ./constants (SSOT)

// Bonus table (matches SQL: v_bonus_table ARRAY[0,0,200,300,400,500,600,700,1000,2000,3000,0,5000])
const BONUS_TABLE = [0, 0, 200, 300, 400, 500, 600, 700, 1000, 2000, 3000, 0, 5000];

const SECTOR_LABELS: Record<SectorFilter, string> = {
  closer: "Closers",
  setter: "Setters",
  dialer: "Dialers",
};

// Per-rep day journey + Federer stats moved to ./scoreBreakdownUtils
// (2026-07-04) — shared with RepBreakdownPanel's inline row expansion.

// --- Compute score breakdown for a given sector ---
function computeBreakdown(entries: LeaderboardEntry[], sector: SectorFilter) {
  if (entries.length === 0) return [];

  // Find max values (denominators for normalization)
  const safeMax = (vals: number[]) => Math.max(...vals, 0.001);

  // show_rate now factors in orphan_precalls per the 2026-04-28 leaderboard ETL fix —
  // calculate_rankings uses the same formula, so display matches ranking.
  const metrics = entries.map((e) => {
    // CPC mirrors calculate_rankings.sql exactly (2026-04-28 fix):
    //   setter:        cc_total / calls_booked (0 if no pre-calls filed)
    //   closer/dialer: cc_total / shows        (0 if no shows)
    // paying_installment does NOT count as a show or call taken — cash only.
    const cpc =
      sector === "setter"
        ? e.calls_booked > 0
          ? e.cc_total / e.calls_booked
          : 0
        : e.shows > 0
          ? e.cc_total / e.shows
          : 0;
    return { ...e, cpc };
  });

  const maxCC = safeMax(metrics.map((e) => e.cc_total || 0));
  const maxSR = safeMax(metrics.map((e) => e.show_rate || 0));
  const maxCPC = safeMax(metrics.map((e) => e.cpc));
  const maxCB = safeMax(metrics.map((e) => e.calls_booked || 0));

  return metrics
    .map((e) => {
      const ccN = (e.cc_total || 0) / maxCC;
      const srN = (e.show_rate || 0) / maxSR;
      const cpcN = e.cpc / maxCPC;
      const cbN = (e.calls_booked || 0) / maxCB;

      // Pre-compute SR denominator + raw values for tooltip formulas.
      // The hover tooltip shows "Show Rate = 17 / (17+1+8) = 65.4%" — readers see
      // the live numerator and denominator, not just the percentage.
      const orphans = (e as { orphan_precalls?: number }).orphan_precalls ?? 0;
      const srDenom = (e.shows || 0) + (e.no_shows || 0) + orphans;
      const cpcDenom = sector === "setter" ? e.calls_booked || 0 : e.shows || 0;
      const cpcDenomLabel = sector === "setter" ? "calls_booked" : "shows";

      let components: {
        label: string;
        raw: string;
        norm: number;
        weight: number;
        contribution: number;
        formula: string;
        computation: string;
      }[];
      let score: number;

      if (sector === "closer") {
        components = [
          {
            label: "Cash",
            raw: formatUSD(e.cc_total),
            norm: ccN,
            weight: 0.5,
            contribution: ccN * 0.5,
            formula: "Cash = SUM(transactions.amount) for cash-collected events",
            computation: `${formatUSD(e.cc_total)} ÷ ${formatUSD(maxCC)} (best) = ${ccN.toFixed(2)}`,
          },
          {
            label: "Show Rate",
            raw: formatPercent((e.show_rate || 0) * 100),
            norm: srN,
            weight: 0.25,
            contribution: srN * 0.25,
            formula: "Show Rate = shows / (shows + no_shows + orphan_precalls)",
            computation: `${e.shows || 0} / (${e.shows || 0} + ${e.no_shows || 0} + ${orphans}) = ${srDenom > 0 ? (((e.shows || 0) / srDenom) * 100).toFixed(1) : "0"}%`,
          },
          {
            label: "CPC",
            raw: formatUSD(e.cpc),
            norm: cpcN,
            weight: 0.25,
            contribution: cpcN * 0.25,
            formula: `CPC = cc_total / ${cpcDenomLabel}`,
            computation: `${formatUSD(e.cc_total)} / ${cpcDenom} = ${formatUSD(e.cpc)}`,
          },
        ];
        score = ccN * 0.5 + srN * 0.25 + cpcN * 0.25;
      } else if (sector === "setter") {
        components = [
          {
            label: "CPC",
            raw: formatUSD(e.cpc),
            norm: cpcN,
            weight: 0.4,
            contribution: cpcN * 0.4,
            formula: `CPC = cc_total / ${cpcDenomLabel}`,
            computation: `${formatUSD(e.cc_total)} / ${cpcDenom} = ${formatUSD(e.cpc)}`,
          },
          {
            label: "Show Rate",
            raw: formatPercent((e.show_rate || 0) * 100),
            norm: srN,
            weight: 0.3,
            contribution: srN * 0.3,
            formula: "Show Rate = shows / (shows + no_shows + orphan_precalls)",
            computation: `${e.shows || 0} / (${e.shows || 0} + ${e.no_shows || 0} + ${orphans}) = ${srDenom > 0 ? (((e.shows || 0) / srDenom) * 100).toFixed(1) : "0"}%`,
          },
          {
            label: "Booked",
            raw: String(e.calls_booked || 0),
            norm: cbN,
            weight: 0.3,
            contribution: cbN * 0.3,
            formula: "Booked = COUNT(sales_pre_call) for the rep × range",
            computation: `${e.calls_booked || 0} ÷ ${maxCB} (best) = ${cbN.toFixed(2)}`,
          },
        ];
        score = cpcN * 0.4 + srN * 0.3 + cbN * 0.3;
      } else {
        // Dialer mirrors the setter (2026-06-02, Session 25; reconfirmed R54
        // 2026-08-17): 40% CPC, 30% Show Rate, 30% Booked. Dials + CTB dropped —
        // daily_dials and connect_to_book_rate are never populated live; see
        // dial.connectToBook / lb.connectToBookRate in metricMeta.ts.
        components = [
          {
            label: "CPC",
            raw: formatUSD(e.cpc),
            norm: cpcN,
            weight: 0.4,
            contribution: cpcN * 0.4,
            formula: `CPC = cc_total / ${cpcDenomLabel}`,
            computation: `${formatUSD(e.cc_total)} / ${cpcDenom} = ${formatUSD(e.cpc)}`,
          },
          {
            label: "Show Rate",
            raw: formatPercent((e.show_rate || 0) * 100),
            norm: srN,
            weight: 0.3,
            contribution: srN * 0.3,
            formula: "Show Rate = shows / (shows + no_shows + orphan_precalls)",
            computation: `${e.shows || 0} / (${e.shows || 0} + ${e.no_shows || 0} + ${orphans}) = ${srDenom > 0 ? (((e.shows || 0) / srDenom) * 100).toFixed(1) : "0"}%`,
          },
          {
            label: "Booked",
            raw: String(e.calls_booked || 0),
            norm: cbN,
            weight: 0.3,
            contribution: cbN * 0.3,
            formula: "Booked = COUNT(sales_pre_call) for the rep × range (solo bookings)",
            computation: `${e.calls_booked || 0} ÷ ${maxCB} (best) = ${cbN.toFixed(2)}`,
          },
        ];
        score = cpcN * 0.4 + srN * 0.3 + cbN * 0.3;
      }

      return {
        rep_id: e.rep_id,
        rep_name: e.rep_name,
        rank: e.rank,
        score,
        components,
      };
    })
    .sort((a, b) => b.score - a.score);
}

// --- Daily points aggregation ---
function computeDailyPoints(leaderboard: LeaderboardEntry[], sector: SectorFilter) {
  // Group by date, find rank order per date
  const dateMap = new Map<string, LeaderboardEntry[]>();
  leaderboard
    .filter((e) => e.sector === sector && e.rank != null)
    .forEach((e) => {
      const existing = dateMap.get(e.date) || [];
      existing.push(e);
      dateMap.set(e.date, existing);
    });

  const days = Array.from(dateMap.entries())
    .map(([date, entries]) => {
      const sorted = entries.sort((a, b) => (a.rank ?? 999) - (b.rank ?? 999));
      return {
        date,
        entries: sorted.slice(0, 4).map((e) => ({
          name: e.rep_name,
          rank: e.rank,
          points: e.points_earned,
        })),
      };
    })
    .sort((a, b) => b.date.localeCompare(a.date));

  return days;
}

// =============================================================================
// MAIN DRAWER COMPONENT
// =============================================================================
const CalculationsDrawer: React.FC<CalculationsDrawerProps> = ({
  isOpen,
  onClose,
  activeSector,
  sectorEntries,
  activeLeaderboard,
  dateLabel,
  timezone = "UTC",
}) => {
  // Derive cohort context for CohortRecoveryCard. Period = month-bucket of the
  // earliest date in the active range. Computed BEFORE the !isOpen early
  // return so the hook order stays stable across mounts.
  const cohortContext = useMemo(() => {
    if (!activeLeaderboard.length) return null;
    const dates = activeLeaderboard
      .map((e) => e.date)
      .filter((d): d is string => Boolean(d))
      .sort();
    if (!dates.length) return null;
    const orgId = activeLeaderboard[0]?.organization_id;
    if (!orgId) return null;
    const firstDate = dates[0];
    const periodStart = firstDate.slice(0, 7) + "-01";
    return { orgId, periodStart };
  }, [activeLeaderboard]);

  if (!isOpen) return null;

  const entries = sectorEntries[activeSector] || [];
  const breakdown = computeBreakdown(entries, activeSector);
  const dailyPoints = computeDailyPoints(activeLeaderboard, activeSector);

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/30 z-40 animate-fade-in" onClick={onClose} />

      {/* Drawer */}
      <div className="fixed right-0 top-0 bottom-0 w-full sm:w-[70%] bg-white z-50 shadow-2xl overflow-y-auto animate-slide-in-right">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-black/[0.06] px-5 py-4 flex items-center justify-between z-10">
          <div>
            <h2 className="token-18 text-glass">Calculations</h2>
            <p className="token-13 text-glass-muted mt-0.5">
              How every score, rank, and point is calculated
            </p>
          </div>
          <button
            onClick={onClose}
            className="anim-tap p-2 text-glass-muted hover:text-glass transition-colors rounded-lg hover:bg-black/[0.04]"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Sections */}
        <div>
          {/* ============================================================
              SECTION 1: HOW RANKINGS WORK
              ============================================================ */}
          <Section
            title="HOW RANKINGS WORK"
            subtitle="The formula behind every rep's score"
            defaultOpen={true}
          >
            <div className="space-y-4">
              <p className="token-13 text-glass-secondary leading-relaxed">
                Each rep gets a <strong>weighted score (0-1)</strong>. Highest score = #1. Each
                metric is normalized against the best performer, then multiplied by its weight.
              </p>

              <div className="p-2 rounded-md bg-black/[0.02] border border-black/[0.06]">
                <p className="token-13 text-glass-muted uppercase">How normalization works</p>
                <p className="token-13 text-glass-secondary mt-0.5 leading-relaxed">
                  "Normalized" = your value / best value. Beryl $160K, Rayston $22K → Beryl = 1.00,
                  Rayston = 0.14.
                </p>
              </div>

              <p className="token-13 text-glass-muted uppercase mt-4">
                {SECTOR_LABELS[activeSector]} — Weight Distribution
              </p>
              <WeightBlocks weights={SECTOR_WEIGHTS[activeSector]} />

              <p className="token-13 text-glass-muted uppercase mt-4">All sectors</p>
              {(["closer", "setter", "dialer"] as SectorFilter[])
                .filter((s) => s !== activeSector)
                .map((s) => (
                  <div key={s} className="mt-2">
                    <p className="token-13 text-glass-secondary font-semibold">
                      {SECTOR_LABELS[s]}
                    </p>
                    <div className="flex gap-1 mt-1">
                      {SECTOR_WEIGHTS[s].map((w) => (
                        <span
                          key={w.label}
                          className="token-13 text-glass-muted bg-black/[0.03] px-2 py-1 rounded"
                        >
                          {w.pct}% {w.label}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}

              <Note>
                Formula: score = (metric1 / best_metric1 x weight1) + (metric2 / best_metric2 x
                weight2) + ... The weights above add up to 100%. A rep who leads every metric scores
                1.00 (perfect).
              </Note>

              {/* Nathaniel quotes — ranking */}
              <blockquote className="pl-3 border-l-2 border-ds-accent/40">
                <p className="token-11 text-glass-muted italic">
                  Nathaniel: "A formula combines all 3 KPIs to determine rank. Reps will know this
                  because it's in the handbook."
                </p>
              </blockquote>
              <blockquote className="pl-3 border-l-2 border-ds-accent/40">
                <p className="token-11 text-glass-muted italic">
                  Nathaniel: "Daniel has $500K CC. Jack has $300K CC. But Jack is ranked HIGHER.
                  Why? Jack has better Show Rate or CPC."
                </p>
              </blockquote>

              {/* Nathaniel quotes — what each sector tracks */}
              <p className="token-13 text-glass-muted uppercase mt-3">What each sector tracks</p>
              <blockquote className="pl-3 border-l-2 border-ds-accent/40">
                <p className="token-11 text-glass-muted italic">
                  Nathaniel on Closers: "CC (Cash Collected), Show Rate (target 70%+), CPC (Cash Per
                  Call). On Altara, closers show CC + Show Rate + CPC."
                </p>
              </blockquote>
              <blockquote className="mt-1 pl-3 border-l-2 border-ds-accent/40">
                <p className="token-11 text-glass-muted italic">
                  Nathaniel on Setters: "CPC, Show Rate, Calls Booked. These three things reward
                  both quality AND quantity. A setter who books 10 calls at 70% show with high CPC
                  will outrank a setter who books 20 calls at 40% show with low CPC."
                </p>
              </blockquote>
              <blockquote className="mt-1 pl-3 border-l-2 border-ds-accent/40">
                <p className="token-11 text-glass-muted italic">
                  Nathaniel on Dialers: "CPC, Daily Dials (minimum 180/day = 900/week), CTB
                  (Connect-to-Book, target 5-10%), Show Rate (target 70%+)."
                </p>
              </blockquote>

              {/* Nathaniel — leaderboard vs full KPIs */}
              <blockquote className="mt-2 pl-3 border-l-2 border-ds-accent/40">
                <p className="token-11 text-glass-muted italic">
                  Nathaniel: "Leaderboard KPIs determine your rank. Full KPIs determine your job
                  performance. Closers full KPIs add Close Rate, Reschedule Rate, Follow-Up
                  Conversion, Refund Rate. Setters add Qualified Show Rate, Booked-to-Conversion,
                  Pre-Call Compliance. Dialers add Connection Rate, Callback Attempts, Pre-Call
                  Compliance."
                </p>
              </blockquote>
            </div>
          </Section>

          {/* ============================================================
              SECTION 2: LIVE SCORE BREAKDOWN
              ============================================================ */}
          <Section
            title={`SCORE BREAKDOWN — ${SECTOR_LABELS[activeSector].toUpperCase()}`}
            subtitle={`Live data for ${dateLabel}`}
          >
            {breakdown.length === 0 ? (
              <p className="token-13 text-glass-muted">
                No {SECTOR_LABELS[activeSector].toLowerCase()} data for this period.
              </p>
            ) : (
              <div className="space-y-4">
                <p className="token-13 text-glass-secondary">
                  This table shows the exact math for each rep's score. Every raw value, its
                  normalized form (divided by the best), and how much it contributes to the final
                  score.
                </p>

                {/* Max values callout */}
                <div className="p-3 rounded-lg bg-black/[0.02] border border-black/[0.06]">
                  <p className="token-13 text-glass-muted uppercase">Best values (denominators)</p>
                  <div className="flex gap-4 mt-1">
                    {breakdown[0]?.components.map((c) => {
                      // Find the max raw value for this metric
                      const allRaw = breakdown.map((b) => {
                        const comp = b.components.find((bc) => bc.label === c.label);
                        return comp?.norm || 0;
                      });
                      const maxIdx = allRaw.indexOf(Math.max(...allRaw));
                      const maxEntry = breakdown[maxIdx]?.components.find(
                        (bc) => bc.label === c.label
                      );
                      return (
                        <span key={c.label} className="token-15 text-glass-secondary">
                          {c.label}: {maxEntry?.raw || "—"}
                        </span>
                      );
                    })}
                  </div>
                </div>

                {/* Per-rep breakdown */}
                {breakdown.map((rep, repIdx) => {
                  const journey = computeRepJourney(activeLeaderboard, rep.rep_id, activeSector);
                  const federer = computeFederer(activeLeaderboard, rep.rep_id, activeSector);
                  return (
                    <div
                      key={rep.rep_name}
                      className={`p-4 rounded-xl border ${
                        repIdx === 0
                          ? "border-amber-300/30 bg-amber-50/30"
                          : "border-black/[0.06] bg-black/[0.01]"
                      }`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <span className="token-15 text-glass font-bold">#{rep.rank}</span>
                          <span className="token-13 text-glass font-semibold">{rep.rep_name}</span>
                        </div>
                        <span className="token-15 text-glass">{rep.score.toFixed(3)}</span>
                      </div>

                      {/* Federer-effect callout — leadership days + tightest gap */}
                      {federer.totalDays > 0 && (
                        <div className="mb-3 p-2 rounded-md bg-black/[0.02] border border-black/[0.04]">
                          <p className="token-13 text-glass-secondary">
                            Led on{" "}
                            <span className="text-ds-accent font-semibold">{federer.leadDays}</span>{" "}
                            of {federer.totalDays} days
                            {federer.tightestGap && (
                              <>
                                {" — tightest gap "}
                                <span className="text-glass">
                                  {formatDateKeyDisplay(federer.tightestGap.date, timezone)}
                                </span>{" "}
                                ({(federer.tightestGap.myScore * 100).toFixed(0)}% vs{" "}
                                {federer.tightestGap.nextRep}{" "}
                                {(federer.tightestGap.nextScore * 100).toFixed(0)}%)
                              </>
                            )}
                          </p>
                        </div>
                      )}

                      {/* Fixed-width metric columns exceed mobile drawer width even at w-full —
                          contain in its own scroll area rather than squeezing/overlapping. */}
                      <div className="overflow-x-auto">
                        <div className="grid gap-2 min-w-[380px]">
                          {rep.components.map((c) => (
                            <HoverTooltip
                              key={c.label}
                              label={c.label}
                              formula={c.formula}
                              computation={c.computation}
                            >
                              <div className="flex items-center gap-3 py-1.5 border-b border-black/[0.04] last:border-b-0 cursor-help hover:bg-black/[0.02] rounded">
                                <span className="token-13 text-glass-muted w-16 shrink-0 uppercase">
                                  {c.label}
                                </span>
                                <span className="token-15 text-glass w-20 shrink-0 text-right">
                                  {c.raw}
                                </span>
                                <span className="token-13 text-glass-muted w-12 shrink-0 text-right">
                                  norm: {c.norm.toFixed(2)}
                                </span>
                                <span className="token-13 text-glass-muted w-10 shrink-0 text-center">
                                  x {(c.weight * 100).toFixed(0)}%
                                </span>
                                <span className="token-13 text-glass-muted shrink-0">=</span>
                                <span className="token-15 text-ds-accent w-10 shrink-0 text-right">
                                  {c.contribution.toFixed(2)}
                                </span>
                                <div className="flex-1 min-w-[60px] h-1.5 rounded-full bg-black/[0.06] overflow-hidden">
                                  <div
                                    className="h-full rounded-full bg-ds-accent/60"
                                    style={{ width: `${Math.round(c.norm * 100)}%` }}
                                  />
                                </div>
                              </div>
                            </HoverTooltip>
                          ))}
                        </div>
                      </div>

                      {/* Cohort recovery sidecar — late resolutions for this rep + period */}
                      {cohortContext && (
                        <CohortRecoveryCard
                          orgId={cohortContext.orgId}
                          repId={rep.rep_id}
                          periodStart={cohortContext.periodStart}
                        />
                      )}

                      {/* Day-by-day journey strip — per-day rank + raw on hover */}
                      {journey.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-black/[0.04]">
                          <p className="token-11 text-glass-muted uppercase mb-2">
                            Daily journey ({journey.length} {journey.length === 1 ? "day" : "days"})
                          </p>
                          <div className="flex flex-wrap gap-1">
                            {journey.map((d) => {
                              const rankColor =
                                d.rank === 1
                                  ? "bg-ds-amber/20 text-ds-amber border-ds-amber/30"
                                  : d.rank === 2
                                    ? "bg-black/[0.05] text-glass-secondary border-black/[0.08]"
                                    : d.rank === 3
                                      ? "bg-black/[0.03] text-glass-muted border-black/[0.06]"
                                      : "bg-black/[0.02] text-glass-muted border-black/[0.04]";
                              const tooltipFormula = `Rank ${d.rank ?? "—"} · ${d.rank_points} pts`;
                              const tooltipComputation = `Cash ${formatUSD(d.cash)} · Shows ${d.shows}/${d.shows + d.no_shows + d.orphan_precalls} (${(d.show_rate * 100).toFixed(0)}%) · CPC ${formatUSD(d.cpc)}`;
                              return (
                                <HoverTooltip
                                  key={d.date}
                                  label={formatDateKeyDisplay(d.date, timezone)}
                                  formula={tooltipFormula}
                                  computation={tooltipComputation}
                                >
                                  <div
                                    className={`flex flex-col items-center justify-center w-12 h-12 rounded border cursor-help ${rankColor}`}
                                  >
                                    <span className="token-11 leading-none">{d.date.slice(5)}</span>
                                    <span className="token-13 font-bold leading-tight mt-0.5">
                                      #{d.rank ?? "—"}
                                    </span>
                                    <span className="token-11 leading-none opacity-70">
                                      {d.rank_points}p
                                    </span>
                                  </div>
                                </HoverTooltip>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}

                <Note>
                  All formulas here mirror the live SQL ranking function (calculate_rankings). CPC =
                  cash ÷ shows (calls where the lead actually showed up). Show Rate denominator
                  includes orphan pre-calls — bookings that vanished with no call outcome ever
                  recorded. paying_installment recurring payments don't count as "shows" or "calls
                  taken" — they only contribute to cash.
                </Note>
                <Note>
                  Each rep's component contributions add up to their final score. The rep with the
                  highest score is #1. If the math looks wrong, check the raw data — the leaderboard
                  is only as accurate as the transactions feeding it.
                </Note>
              </div>
            )}
          </Section>

          {/* ============================================================
              SECTION 3: DAILY POINTS LOG
              ============================================================ */}
          <Section title="DAILY POINTS LOG" subtitle="Who got how many points each day this month">
            <div className="space-y-4">
              <p className="token-13 text-glass-secondary">
                Every day, reps earn points based on their rank within their pod and sector. These
                points accumulate throughout the month and determine who wins prestige.
              </p>

              <div className="flex gap-3 mb-3">
                {POINTS_PER_RANK.map((p) => (
                  <div
                    key={p.rank}
                    className="flex items-center gap-1 px-2 py-1 rounded bg-black/[0.03]"
                  >
                    <span className="token-13 text-glass-secondary font-semibold">{p.rank}</span>
                    <span className="token-15 text-ds-accent">{p.pts} pts</span>
                  </div>
                ))}
              </div>

              {dailyPoints.length === 0 ? (
                <p className="token-13 text-glass-muted">No daily data available.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="token-13 border-b border-black/[0.06]">
                        <th className="px-2 py-2 text-left text-glass-muted uppercase">Date</th>
                        <th className="px-2 py-2 text-left text-glass-muted uppercase">
                          1st (5pt)
                        </th>
                        <th className="px-2 py-2 text-left text-glass-muted uppercase">
                          2nd (3pt)
                        </th>
                        <th className="px-2 py-2 text-left text-glass-muted uppercase">
                          3rd (2pt)
                        </th>
                        <th className="px-2 py-2 text-left text-glass-muted uppercase">
                          4th (1pt)
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {dailyPoints.slice(0, 31).map((day, idx) => (
                        <tr
                          key={day.date}
                          className="border-b border-black/[0.04] hover:bg-black/[0.02] anim-pop"
                          style={{
                            animationDelay: `${Math.min(idx, 15) * 100}ms`,
                            animationDuration: "480ms",
                          }}
                        >
                          <td className="px-2 py-1.5 token-15 text-glass-secondary">
                            {formatDateKeyDisplay(day.date, timezone)}
                          </td>
                          {[0, 1, 2, 3].map((idx) => (
                            <td key={idx} className="px-2 py-1.5 token-13 text-glass">
                              {day.entries[idx]?.name || "—"}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <Note>
                Points are awarded per pod per sector. If a sector only has 2 reps, there is no 3rd
                or 4th place. Points from all days in the month are summed to determine the monthly
                prestige winner.
              </Note>
            </div>
          </Section>

          {/* ============================================================
              SECTION 4: PRESTIGE SYSTEM — FULL EXPLANATION
              ============================================================ */}
          <Section
            title="PRESTIGE SYSTEM — HOW IT ALL WORKS"
            subtitle="The complete system, from scratch"
          >
            <div className="space-y-5">
              {/* What is prestige */}
              <div>
                <p className="token-13 text-glass-muted uppercase">What is prestige?</p>
                <p className="token-13 text-glass-secondary mt-1">
                  Prestige is a <strong>permanent rank from 0 to 12</strong> that reps earn over
                  time. Think of it like Call of Duty prestige — you level up by winning monthly
                  competitions. Unlike daily leaderboard rank (which resets), prestige{" "}
                  <strong>carries across months</strong>.
                </p>
                <blockquote className="mt-2 pl-3 border-l-2 border-ds-accent/40">
                  <p className="token-11 text-glass-muted italic">
                    Nathaniel: "The leaderboard is day-to-day motivation that contributes to the
                    prestige system. The prestige system is a monthly points system where the rep
                    with the highest points gets a bonus."
                  </p>
                </blockquote>
              </div>

              {/* 3 point sources */}
              <div>
                <p className="token-13 text-glass-muted uppercase">3 point sources (monthly)</p>
                <div className="space-y-2 mt-2">
                  <div className="flex gap-3 p-3 rounded-lg bg-black/[0.02] border border-black/[0.06]">
                    <div className="token-15 text-ds-accent w-6">1</div>
                    <div>
                      <p className="token-13 text-glass font-semibold">Daily Leaderboard Points</p>
                      <p className="token-13 text-glass-secondary">
                        #1 gets 5 pts, #2 gets 3, #3 gets 2, #4 gets 1. Summed across all days in
                        the month. This is the biggest source. A top performer earns 80-120
                        pts/month.
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-3 p-3 rounded-lg bg-black/[0.02] border border-black/[0.06]">
                    <div className="token-15 text-ds-accent w-6">2</div>
                    <div>
                      <p className="token-13 text-glass font-semibold">KPI Compliance</p>
                      <p className="token-13 text-glass-secondary">
                        Daily binary check: did you hit ALL your KPIs? Yes = +1 point. No = -1
                        point. Range: -30 to +30 per month (60-point swing).
                      </p>
                      <div className="mt-2 space-y-1">
                        <p className="token-13 text-glass-muted">
                          Closer KPIs: Show Rate &ge; 70%, Close Rate &ge; 50%
                        </p>
                        <p className="token-13 text-glass-muted">Setter KPIs: Show Rate &ge; 70%</p>
                        <p className="token-13 text-glass-muted">
                          Dialer KPIs: Connect-to-Book &ge; 5%
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-3 p-3 rounded-lg bg-black/[0.02] border border-black/[0.06]">
                    <div className="token-15 text-ds-accent w-6">3</div>
                    <div>
                      <p className="token-13 text-glass font-semibold">MVP Votes</p>
                      <p className="token-13 text-glass-secondary">
                        Last 2 days of the month, each rep votes for one teammate. Each vote = 1
                        point for the nominee. You can vote cross-sector (a closer can vote for a
                        setter). Cannot vote for yourself. Typical range: 0-15 pts.
                      </p>
                      <blockquote className="mt-2 pl-3 border-l-2 border-ds-accent/40">
                        <p className="token-11 text-glass-muted italic">
                          Nathaniel: "You cannot vote for yourself. You get one vote per person. You
                          can vote for anyone on your team from any sector. Voting is anonymous so
                          no one can see who voted for who. The voting window is the last 2 days of
                          the month (Day 29-30)."
                        </p>
                      </blockquote>
                    </div>
                  </div>
                </div>

                <div className="mt-3 p-3 rounded-lg bg-black/[0.03] border border-black/[0.06]">
                  <p className="token-13 text-glass-secondary">
                    <strong>Total = Leaderboard + KPI + MVP.</strong> The rep with the highest total
                    in their <strong>sector within their pod</strong> wins. There is 1 winner per
                    sector per pod per month (e.g., 3 pods x 3 sectors = 9 winners).
                  </p>
                </div>
                <blockquote className="mt-2 pl-3 border-l-2 border-ds-accent/40">
                  <p className="token-11 text-glass-muted italic">
                    Nathaniel: "Top of the daily leaderboard is 5 points — this will be the biggest
                    point generator. KPI compliance: everyday you hit KPI is 1 point, if you don't
                    hit KPI then you lose a point. And lastly each team will vote for an MVP — they
                    can't vote for themselves but they can vote for anyone on the team. This will be
                    rewarded 1 point for every rep that votes you."
                  </p>
                </blockquote>
                <blockquote className="mt-2 pl-3 border-l-2 border-ds-accent/40">
                  <p className="token-11 text-glass-muted italic">
                    Nathaniel: "The prestige system is a pod thing. If there are 3 pods, there will
                    be 3 different winners each month from each sector. With 3 pods and 3 sectors,
                    that equals 9 winners company-wide every single month."
                  </p>
                </blockquote>
              </div>

              {/* Winning and de-ranking */}
              <div>
                <p className="token-13 text-glass-muted uppercase">
                  What happens when you win (or don't)
                </p>
                <div className="mt-2 space-y-2">
                  <div className="flex items-start gap-2 p-2 rounded bg-ds-green-bg">
                    <span className="token-13 text-ds-green font-bold">WIN</span>
                    <p className="token-13 text-glass-secondary">
                      You rank up +1 level. Buffer resets to 0. First time reaching a level = cash
                      bonus.
                    </p>
                  </div>
                  <div className="flex items-start gap-2 p-2 rounded bg-ds-amber-bg">
                    <span className="token-13 text-ds-amber font-bold">MISS 1</span>
                    <p className="token-13 text-glass-secondary">
                      Grace period. No rank change. Buffer set to 1. You get one free month.
                    </p>
                  </div>
                  <div className="flex items-start gap-2 p-2 rounded bg-ds-red-bg">
                    <span className="token-13 text-ds-red font-bold">MISS 2</span>
                    <p className="token-13 text-glass-secondary">
                      De-rank: you lose 1 level. Buffer resets. You get another grace period after.
                      De-ranking is 1 level at a time, never straight to 0. Minimum is level 0.
                    </p>
                  </div>
                </div>

                <Note>
                  Example: Beryl wins month 1 (goes from P2 to P3). Month 2 she doesn't win (buffer
                  used, stays P3). Month 3 she doesn't win again (de-ranks to P2, buffer resets).
                  Month 4 she doesn't win (buffer used again, stays P2). The cycle repeats.
                </Note>
                <blockquote className="mt-2 pl-3 border-l-2 border-ds-accent/40">
                  <p className="token-11 text-glass-muted italic">
                    Nathaniel: "If you aren't the winner twice in a row, you get de-ranked. When you
                    de-rank, it's only 1 rank at a time, not all the way to the start. So if you're
                    rank 6, you'll go down to 5, not 0."
                  </p>
                </blockquote>
                <blockquote className="mt-2 pl-3 border-l-2 border-ds-accent/40">
                  <p className="token-11 text-glass-muted italic">
                    Nathaniel: "Without de-ranking, someone would just need to win 12 times and
                    they're done. With de-ranking, people are forced to keep the pressure on. You
                    can't coast after achieving prestige. It creates golden handcuffs because you
                    don't want to lose all the progress you've built."
                  </p>
                </blockquote>
                <blockquote className="mt-2 pl-3 border-l-2 border-ds-accent/40">
                  <p className="token-11 text-glass-muted italic">
                    Nathaniel: "Think of it like Clash Royale or Clash of Clans — if you lose a
                    battle you get trophies taken away and then when you go beyond a certain amount
                    of trophies you get de-ranked."
                  </p>
                </blockquote>
              </div>

              {/* Elite levels */}
              <div>
                <p className="token-13 text-glass-muted uppercase">
                  Elite levels (P6-P12) are permanent
                </p>
                <p className="token-13 text-glass-secondary mt-1">
                  Once a rep reaches P6 or above, that{" "}
                  <strong>highest level is locked forever</strong> on their profile — even if they
                  de-rank back to P3. The badge shows their highest achieved level as a permanent
                  emblem. Below P6 (stars 1-5), levels are fully temporary.
                </p>
                <blockquote className="mt-2 pl-3 border-l-2 border-ds-accent/40">
                  <p className="token-11 text-glass-muted italic">
                    Nathaniel: "Stars (Prestige 1-5) are temporary. If you de-rank, the stars are
                    removed immediately. Elite Emblems (Prestige 6-12) are permanent. If you reach
                    Prestige 9, then later de-rank to 8, your profile still displays the Prestige 9
                    Emblem to honor the fact that you achieved that level."
                  </p>
                </blockquote>
              </div>

              {/* Bonus table */}
              <div>
                <p className="token-13 text-glass-muted uppercase">
                  Cash bonuses (first time only)
                </p>
                <p className="token-13 text-glass-secondary mt-1 mb-2">
                  Bonuses are paid only the FIRST time a rep reaches each level. If they de-rank and
                  climb back, no bonus the second time. Total possible: $12,700.
                </p>
                <div className="grid grid-cols-4 gap-1">
                  {BONUS_TABLE.map((bonus, level) => {
                    if (level === 0) return null;
                    return (
                      <div
                        key={level}
                        className={`text-center p-2 rounded ${
                          bonus > 0 ? "bg-ds-green-bg" : "bg-black/[0.02]"
                        }`}
                      >
                        <p className="token-13 text-glass font-semibold">P{level}</p>
                        <p
                          className={`token-15 ${bonus > 0 ? "text-ds-green" : "text-glass-muted"}`}
                        >
                          {bonus > 0 ? formatUSD(bonus) : "—"}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Month-end processing */}
              <div>
                <p className="token-13 text-glass-muted uppercase">Month-end processing</p>
                <p className="token-13 text-glass-secondary mt-1">
                  At month end, the COO manually triggers processing. The system: (1) tallies all
                  three point sources, (2) determines winners per sector per pod, (3) applies
                  rank-up or de-rank logic, (4) records history, (5) notifies via Slack. This is
                  currently manual (not automated) so the COO can verify before announcing.
                </p>
                <blockquote className="mt-2 pl-3 border-l-2 border-ds-accent/40">
                  <p className="token-11 text-glass-muted italic">
                    Nathaniel: "On Day 1 of the new month, the COO posts the announcement in the
                    #prestige-winners channel. The announcement includes each pod broken out
                    separately, showing the winner for each sector, their prestige change, their
                    bonus, and their total points for the month."
                  </p>
                </blockquote>
                <blockquote className="mt-2 pl-3 border-l-2 border-ds-accent/40">
                  <p className="token-11 text-glass-muted italic">
                    Nathaniel: "The idea of this system is it almost like a golden handcuff for
                    keeping sales reps here. 1-5 they get hooked in, 5-10 they are grinding, then
                    11-12 they are attached and locked in."
                  </p>
                </blockquote>
              </div>

              <Note>
                If anything looks wrong in the prestige standings, check the Daily Points Log
                (Section 3 above) to find the specific day with the discrepancy. Points come from
                daily_leaderboard, which comes from transactions. Fix the transaction, re-run the
                leaderboard rollup, and the points will correct.
              </Note>
            </div>
          </Section>
        </div>
      </div>
    </>
  );
};

export default CalculationsDrawer;
